import { ContactPointSchema } from '@app/icare/db/schema/common'
import { BiaMeasurementFormSchema } from '@app/icare/db/schema/form/BiaMeasurement'
import { PatientFormCommonSchema } from '@app/icare/db/schema/form/PatientFormCommon'
import type { PatientFormTypeType, PatientSignedFormTypeType } from '@app/icare/db/schema/form/PatientFormType'
import { PatientFormTypeEnum, PatientSignedFormType } from '@app/icare/db/schema/form/PatientFormType'
import { PatientMasterDataSheetSchema } from '@app/icare/db/schema/form/PatientMasterDataSheet'
import type { PatientSupplyForm } from '@app/icare/db/schema/form/PatientSupplyForm'
import { PatientSupplyFormSchema, PatientSupplyStatusEnum } from '@app/icare/db/schema/form/PatientSupplyForm'
import { ProgressReportFormSchema } from '@app/icare/db/schema/form/ProgressReportForm'
import type { Article } from '@app/icare/entities/article/schema/Article'
import { ArticleId } from '@app/icare/entities/article/schema/Article'
import type { ArticleCategory } from '@app/icare/entities/article/schema/ArticleCategory'
import { MedicalTechnologySchema } from '@app/icare/entities/medicalTechnology/schema/MedicalTechnology'
import type { Patient } from '@app/icare/entities/patient/schema/Patient'
import { PatientTherapyId } from '@app/icare/entities/patient/schema/PatientTherapy'
import { PharmacySchema } from '@app/icare/entities/pharmacy/schema/Pharmacy'
import { msg } from '@lingui/core/macro'
import { entityId } from '@pkg/db/schema'
import { formatDate, formatDateTime, mergeNonEmptyStrings } from '@pkg/shared/utils'
import { defineEnum } from '@pkg/shared/utils/enum.utils'
import { normalizeFileName } from '@pkg/shared/utils/file.utils'
import { z } from 'zod'

export const SupplyEnum = defineEnum({
  NEUVERSORGUNG: msg`Neuversorgung`,
  FOLGEVERSORGUNG: msg`Folgeversorgung`,
})

export type SupplyType = typeof SupplyEnum.$.Type

export const AddressSourceEnum = defineEnum({
  PATIENT: msg`Patient`,
  PATIENT_DELIVERY_ADDRESS: msg`Lieferadresse`,
  PATIENT_CONTACTABLE_ADDRESS: msg`Kontaktadresse`,
})

export type AddressSourceType = typeof AddressSourceEnum.$.Type

const PatientOrderDeliveryAddressSchema = z.object({
  source: z.enum(AddressSourceEnum.$.zodEnum()).nullish(),
  value: ContactPointSchema.nullish(),
})

export type PatientOrderDeliveryAddress = z.infer<typeof PatientOrderDeliveryAddressSchema>

export const TimeRangeSchema = z.object({
  withTime: z.boolean().nullish().default(false),
  from: z.iso.datetime().nullish().default(null),
  to: z.iso.datetime().nullish().default(null),
})

const PatientCommonOrderSchema = PatientFormCommonSchema.extend({
  // therapy: PatientTherapyRefSchema.nullish(),
  therapyId: PatientTherapyId.ref,
})

const OrderArticleSchema = z.object({
  amount: z.coerce.number().nullish(),
  articleId: ArticleId.ref,
  // article: ArticleSchema,
})

export type OrderArticle = z.infer<typeof OrderArticleSchema>

export const CustomArticleId = entityId('customArticle')

export type CustomArticleIdType = typeof CustomArticleId.Type

const OrderCustomArticleSchema = z.object({
  id: CustomArticleId.ref.nullish(),
  amount: z.coerce.number().nullish(),
  name: z.string().nullish(),
  pzn: z.string().nullish(),
})

export type OrderCustomArticle = z.infer<typeof OrderCustomArticleSchema>

export interface RelatedArticleOrCategoryNotAddedWarning {
  code: 'custom'
  message: 'related_article_or_category_not_added'
  params: {
    missingArticles: Article[]
    missingCategories: ArticleCategory[]
  }
}

export const OrderValidationSchema = z.object({
  supply: z.enum(SupplyEnum.$.zodEnum()),
  managedOrder: z.boolean().default(false),
  articles: z.array(OrderArticleSchema).default(() => []),
  customArticles: z.array(OrderCustomArticleSchema).default(() => []),
  comments: z
    .object({
      missingArticles: z.string().nullish(),
    })
    .default({}),
  comment: z.string().nullish(),
  deliveryTime: TimeRangeSchema.nullish(),
})

export type Order = z.infer<typeof OrderValidationSchema>

export const AntibiosisOrderStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`in Bearbeitung`,
  approved: msg`Fertiggestellt`,
  cancelled: msg`Storniert`,
})

export type AntibiosisOrderStatusType = typeof AntibiosisOrderStatusEnum.$.Type

export type TimeRange = z.infer<typeof TimeRangeSchema>

export const AntibiosisOrderSchema = PatientCommonOrderSchema.extend({
  status: z.enum(AntibiosisOrderStatusEnum.$.zodEnum()).default(AntibiosisOrderStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.AntibiosisOrder).default(PatientFormTypeEnum.AntibiosisOrder),
  form: OrderValidationSchema.extend({
    released: z.boolean().default(false),
    conditionsAccepted: z.boolean().default(false),
    formFilled: z.boolean().default(false),
    instructionsGiven: z.boolean().default(false),
    shipConfidoBox: z.boolean().default(false),
    firstDeliveryLength: z.coerce.number().nullish(),
    pickupTime: TimeRangeSchema.default(() => ({
      date: null,
      from: null,
      to: null,
      withTime: false,
    })),
    stationaryTherapyBeginDate: z.string().datetime().nullish().default(null),
    ambulantTherapyBeginDate: z.string().datetime().nullish().default(null),
    therapyEndDate: z.string().datetime().nullish().default(null),
    deliveryAddress: PatientOrderDeliveryAddressSchema.nullish(),
    pharmacy: PharmacySchema.pick({
      id: true,
      name: true,
      address: true,
    })
      .nullish()
      .default(null),
  }),
})

export type AntibiosisOrder = z.infer<typeof AntibiosisOrderSchema>

export const EnteralSpecialDietOrderStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`in Bearbeitung`,
  delivered_to_stock: msg`an Lager übergeben`,
  approved: msg`Ware ist versendet`,
  cancelled: msg`Storniert`,
})

export type EnteralSpecialDietOrderStatusType = typeof EnteralSpecialDietOrderStatusEnum.$.Type

const EnteralSpecialDietOrderSchema = PatientCommonOrderSchema.extend({
  status: z.enum(EnteralSpecialDietOrderStatusEnum.$.zodEnum()).default(EnteralSpecialDietOrderStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  form: OrderValidationSchema.extend({
    needsToBeControlled: z.boolean().default(false),
    medicalTechnology: MedicalTechnologySchema.pick({
      id: true,
      name: true,
      address: true,
    })
      .nullish()
      .default(null),
  }),
})

export type EnteralSpecialDietOrder = z.infer<typeof EnteralSpecialDietOrderSchema>

export const EnteralTubeFeedingOrderStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`in Bearbeitung`,
  delivered_to_stock: msg`an Lager übergeben`,
  approved: msg`Ware ist versendet`,
  cancelled: msg`Storniert`,
})

export type EnteralTubeFeedingOrderStatusType = typeof EnteralTubeFeedingOrderStatusEnum.$.Type

export const EnteralTubeFeedingOrderSchema = EnteralSpecialDietOrderSchema.extend({
  status: z.enum(EnteralTubeFeedingOrderStatusEnum.$.zodEnum()).default(EnteralTubeFeedingOrderStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.EnteralTubeFeedingOrder).default(PatientFormTypeEnum.EnteralTubeFeedingOrder),
})

export type EnteralTubeFeedingOrder = z.infer<typeof EnteralTubeFeedingOrderSchema>

export const EnteralLiquidFoodOrderStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`in Bearbeitung`,
  delivered_to_stock: msg`an Lager übergeben`,
  approved: msg`Ware ist versendet`,
  cancelled: msg`Storniert`,
})

export type EnteralLiquidFoodOrderStatusType = typeof EnteralLiquidFoodOrderStatusEnum.$.Type

export const EnteralLiquidFoodOrderSchema = EnteralSpecialDietOrderSchema.extend({
  status: z.enum(EnteralLiquidFoodOrderStatusEnum.$.zodEnum()).default(EnteralLiquidFoodOrderStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.EnteralLiquidFoodOrder).default(PatientFormTypeEnum.EnteralLiquidFoodOrder),
})

export type EnteralLiquidFoodOrder = z.infer<typeof EnteralLiquidFoodOrderSchema>

export const HeimparenteralBagsOrderStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`in Bearbeitung`,
  resend_recipe_request: msg`Rezeptanfrage muss erneuert werden`,
  approved: msg`Rezeptanfrage abgeschlossen`,
  cancelled: msg`Storniert`,
})

export type HeimparenteralBagsOrderStatusType = typeof HeimparenteralBagsOrderStatusEnum.$.Type

export const HeimparenteralBagsOrderSchema = PatientCommonOrderSchema.extend({
  status: z.enum(HeimparenteralBagsOrderStatusEnum.$.zodEnum()).default(HeimparenteralBagsOrderStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.HeimparenteralBagsOrder).default(PatientFormTypeEnum.HeimparenteralBagsOrder),
  form: OrderValidationSchema.extend({
    needsToBeControlled: z.boolean().default(false),
    pharmacy: PharmacySchema.pick({
      id: true,
      name: true,
      address: true,
    })
      .nullish()
      .default(null),
    medicalTechnology: MedicalTechnologySchema.pick({
      id: true,
      name: true,
      address: true,
    })
      .nullish()
      .default(null),
  }),
})

export type HeimparenteralBagsOrder = z.infer<typeof HeimparenteralBagsOrderSchema>

export const PainTherapyOrderStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`in Bearbeitung`,
  resend_recipe_request: msg`Rezeptanfrage muss erneuert werden`,
  approved: msg`Ware ist versendet`,
  cancelled: msg`Storniert`,
})

export type PainTherapyOrderStatusType = typeof PainTherapyOrderStatusEnum.$.Type

export const PainTherapyOrderSchema = PatientCommonOrderSchema.extend({
  status: z.enum(PainTherapyOrderStatusEnum.$.zodEnum()).default(PainTherapyOrderStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.PainTherapyOrder).default(PatientFormTypeEnum.PainTherapyOrder),
  form: OrderValidationSchema.extend({
    needsToBeControlled: z.boolean().default(false),
    pharmacy: PharmacySchema.pick({
      id: true,
      name: true,
      address: true,
    })
      .nullish()
      .default(null),
    medicalTechnology: MedicalTechnologySchema.pick({
      id: true,
      name: true,
      address: true,
    })
      .nullish()
      .default(null),
  }),
})

export type PainTherapyOrder = z.infer<typeof PainTherapyOrderSchema>

export const HeimparenteralMedicalDevicesOrderStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`in Bearbeitung`,
  delivered_to_stock: msg`an Lager übergeben`,
  approved: msg`Ware ist versendet`,
  cancelled: msg`Storniert`,
})

export type HeimparenteralMedicalDevicesOrderStatusType = typeof HeimparenteralMedicalDevicesOrderStatusEnum.$.Type

export const HeimparenteralMedicalDevicesOrderSchema = PatientCommonOrderSchema.extend({
  status: z
    .enum(HeimparenteralMedicalDevicesOrderStatusEnum.$.zodEnum())
    .default(HeimparenteralMedicalDevicesOrderStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z
    .literal(PatientFormTypeEnum.HeimparenteralMedicalDevicesOrder)
    .default(PatientFormTypeEnum.HeimparenteralMedicalDevicesOrder),
  form: OrderValidationSchema.extend({
    needsToBeControlled: z.boolean().default(false),
    medicalTechnology: MedicalTechnologySchema.pick({
      id: true,
      name: true,
      address: true,
    })
      .nullish()
      .default(null),
  }),
})

export type HeimparenteralMedicalDevicesOrder = z.infer<typeof HeimparenteralMedicalDevicesOrderSchema>

export const PowerPiccSupplyOrderStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`in Bearbeitung`,
  delivered_to_stock: msg`an Lager übergeben`,
  approved: msg`Ware ist versendet`,
  cancelled: msg`Storniert`,
})

export type PowerPiccSupplyOrderStatusType = typeof PowerPiccSupplyOrderStatusEnum.$.Type

export const PowerPiccSupplyOrderSchema = PatientCommonOrderSchema.extend({
  status: z.enum(PowerPiccSupplyOrderStatusEnum.$.zodEnum()).default(PowerPiccSupplyOrderStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.PowerPiccSupplyOrder).default(PatientFormTypeEnum.PowerPiccSupplyOrder),
  form: OrderValidationSchema.extend({
    needsToBeControlled: z.boolean().default(false),
    medicalTechnology: MedicalTechnologySchema.pick({
      id: true,
      name: true,
      address: true,
    })
      .nullish()
      .default(null),
  }),
})

export type PowerPiccSupplyOrder = z.infer<typeof PowerPiccSupplyOrderSchema>

export const PortIrrigationOrderStatusEnum = defineEnum({
  new: msg`Erstellt`,
  processing: msg`in Bearbeitung`,
  delivered_to_stock: msg`an Lager übergeben`,
  approved: msg`Ware ist versendet`,
  cancelled: msg`Storniert`,
})

export type PortIrrigationOrderStatusType = typeof PortIrrigationOrderStatusEnum.$.Type

export const PortIrrigationOrderSchema = PatientCommonOrderSchema.extend({
  status: z.enum(PortIrrigationOrderStatusEnum.$.zodEnum()).default(PortIrrigationOrderStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.PortIrrigationOrder).default(PatientFormTypeEnum.PortIrrigationOrder),
  form: OrderValidationSchema.extend({
    needsToBeControlled: z.boolean().default(false),
    pharmacy: PharmacySchema.pick({
      id: true,
      name: true,
      address: true,
    })
      .nullish()
      .default(null),
  }),
})

export type PortIrrigationOrder = z.infer<typeof PortIrrigationOrderSchema>

export const PatientInformationStatusEnum = defineEnum({
  new: msg`Neu`,
  approved: msg`Abgeschlossen`,
})

export type PatientInformationStatusType = typeof PatientInformationStatusEnum.$.Type

export const PatientInformationSchema = PatientFormCommonSchema.extend({
  status: z.enum(PatientInformationStatusEnum.$.zodEnum()).default(PatientInformationStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z.literal(PatientFormTypeEnum.PatientInformation).default(PatientFormTypeEnum.PatientInformation),
  form: z.object({
    signature: z.string().nullish(),
  }),
})

export type PatientInformation = z.infer<typeof PatientInformationSchema>

export const PatientConsentAndConfidentialityStatusEnum = defineEnum({
  new: msg`Neu`,
  approved: msg`Abgeschlossen`,
})

export type PatientConsentAndConfidentialityStatusType = typeof PatientConsentAndConfidentialityStatusEnum.$.Type

export const PatientConsentAndConfidentialitySchema = PatientFormCommonSchema.extend({
  status: z
    .enum(PatientConsentAndConfidentialityStatusEnum.$.zodEnum())
    .default(PatientConsentAndConfidentialityStatusEnum.new),
  statusChangedAt: z.string().datetime().nullish().default(null),
  type: z
    .literal(PatientFormTypeEnum.PatientConsentAndConfidentiality)
    .default(PatientFormTypeEnum.PatientConsentAndConfidentiality),
  form: z.object({
    signature: z.string().nullish(),
  }),
})

export type PatientConsentAndConfidentiality = z.infer<typeof PatientConsentAndConfidentialitySchema>

export type PatientSignForm = PatientInformation | PatientConsentAndConfidentiality | PatientSupplyForm

export const SignFormStatusEnum = defineEnum({
  ...PatientInformationStatusEnum.$.messages,
  ...PatientConsentAndConfidentialityStatusEnum.$.messages,
  ...PatientSupplyStatusEnum.$.messages,
})

export type SignFormStatusType = typeof SignFormStatusEnum.$.Type

export type PatientOrderForm =
  | AntibiosisOrder
  | EnteralLiquidFoodOrder
  | EnteralTubeFeedingOrder
  | HeimparenteralBagsOrder
  | HeimparenteralMedicalDevicesOrder
  | PowerPiccSupplyOrder
  | PortIrrigationOrder

export const PatientFormSchema = z.discriminatedUnion('type', [
  AntibiosisOrderSchema,
  BiaMeasurementFormSchema,
  EnteralLiquidFoodOrderSchema,
  EnteralTubeFeedingOrderSchema,
  HeimparenteralBagsOrderSchema,
  HeimparenteralMedicalDevicesOrderSchema,
  PowerPiccSupplyOrderSchema,
  PainTherapyOrderSchema,
  PortIrrigationOrderSchema,
  PatientSupplyFormSchema,
  PatientInformationSchema,
  ProgressReportFormSchema,
  PatientConsentAndConfidentialitySchema,
  PatientMasterDataSheetSchema,
])

export type PatientForm = z.infer<typeof PatientFormSchema>

export const OrderFormSchema = z.discriminatedUnion('type', [
  AntibiosisOrderSchema,
  EnteralLiquidFoodOrderSchema,
  EnteralTubeFeedingOrderSchema,
  HeimparenteralBagsOrderSchema,
  HeimparenteralMedicalDevicesOrderSchema,
  PowerPiccSupplyOrderSchema,
  PainTherapyOrderSchema,
  PortIrrigationOrderSchema,
])

export type OrderForm = z.infer<typeof OrderFormSchema>

export const SignFormSchema = z.discriminatedUnion('type', [
  PatientInformationSchema,
  PatientConsentAndConfidentialitySchema,
  PatientSupplyFormSchema,
])

export type SignForm = z.infer<typeof SignFormSchema>

export const OrderFormStatusEnum = defineEnum({
  ...AntibiosisOrderStatusEnum.$.messages,
  ...EnteralLiquidFoodOrderStatusEnum.$.messages,
  ...EnteralTubeFeedingOrderStatusEnum.$.messages,
  ...HeimparenteralBagsOrderStatusEnum.$.messages,
  ...HeimparenteralMedicalDevicesOrderStatusEnum.$.messages,
  ...PainTherapyOrderStatusEnum.$.messages,
  ...PortIrrigationOrderStatusEnum.$.messages,
})

export type OrderFormStatusType = typeof OrderFormStatusEnum.$.Type

export const generatePatientFormDocumentName = (form: PatientForm, patient: Patient) => {
  const suffix = normalizeFileName(
    mergeNonEmptyStrings(
      [
        patient?.personal?.firstName,
        patient?.personal?.lastName,
        formatDate(patient?.personal?.birthdate),
        form?.createdBy,
        formatDateTime(form?.createdAt),
      ],
      '_',
    ) || '',
  )

  switch (form.type) {
    case PatientFormTypeEnum.PatientInformation:
      return `Aufklaerungsbogen_Versorgung_${suffix}.pdf`

    case PatientFormTypeEnum.PatientSupply:
      return `Versorgungsauftrag_${suffix}.pdf`

    case PatientFormTypeEnum.PatientConsentAndConfidentiality:
      return `Einwilligungs_und_Schweigepflichtentbindungserklaerung_${suffix}.pdf`

    case PatientFormTypeEnum.AntibiosisOrder:
      return `Antibiose_${suffix}.pdf`

    case PatientFormTypeEnum.HeimparenteralBagsOrder:
      return `Heimparenteral_Bags_${suffix}.pdf`

    case PatientFormTypeEnum.PainTherapyOrder:
      return `Schmerztherapie_${suffix}.pdf`

    case PatientFormTypeEnum.HeimparenteralMedicalDevicesOrder:
      return `Heimparenteral_Medical_Devices_${suffix}.pdf`

    case PatientFormTypeEnum.PowerPiccSupplyOrder:
      return `PowerPicc_Versorgung_${suffix}.pdf`

    case PatientFormTypeEnum.EnteralLiquidFoodOrder:
    case PatientFormTypeEnum.EnteralTubeFeedingOrder:
      return `Enteral_Special_Diet_${suffix}.pdf`

    case PatientFormTypeEnum.PortIrrigationOrder:
      return `Port_Irrigation_${suffix}.pdf`

    case PatientFormTypeEnum.MasterDataSheet:
      return `Stammblatt_${suffix}.pdf`

    default:
      throw new Error(`No template has been added for type '${form.type}'`)
  }
}

/**
 * Checks if a form type is an order form type
 * @param type The form type to check
 * @param types The order form types to check against
 * @returns True if the form type is one of the specified order form types
 */
export const isFormType = <T extends PatientFormTypeType>(type: string, ...types: T[]) => {
  return types.includes(type as T)
}

/**
 * Checks if a form type is a signed form type
 * @param type The form type to check
 * @param types The signed form types to check against
 * @returns True if the form type is one of the specified signed form types
 */
export const isSignedFormType = (type: string) => {
  return Object.values(PatientSignedFormType).includes(type as PatientSignedFormTypeType)
}

export const PatientFormDocumentTypeEnum = defineEnum({
  default: msg`Standard`,
  documentAaC: msg`Dokument AaC`,
})

export type PatientFormDocumentTypeType = typeof PatientFormDocumentTypeEnum.$.Type
